import React, {useEffect, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {Modal} from 'react-bootstrap';
import moment from 'moment';
import {useTransfersByAgentContext} from '../contexts/TransfersByAgentContext';
import {usePopupEditTransferContext} from '../contexts/PopupEditTransferContext';
import {useTransferCalculationContext} from '../contexts/TransferCalculationContext';
import {can} from '../../_common/components/Permissions';
import Parameters from './Parameters';
import Calculation from './Calculation';
import Lines from './Lines';
import CustomLines from './CustomLines';
import PopupWarning from './PopupWarning';
import PopupDocsWarning from './PopupDocsWarning';
import DataItemCheckbox from '../../_common/components/DataItemCheckbox';
import styles from './styles.module.scss';

const PopupEditTransfer = ({fullName}) => {
  const [show, setShow] = useState(false);
  const handleShowModal = () => setShow(true);
  const handleCloseModal = () => setShow(false);
  const isEnabled = can('full', 'transfersByAgentPage');
  const {
    formik,
    cellContent,
    getTransferData,
    handleEdit,
    columns,
    tableData,
    getYearsOptions,
    newPaycheck,
    setAllReceiptsChecked,
    updateVat,
  } = usePopupEditTransferContext();
  const {agentId, paycheck, datetime} = cellContent;
  const dateValue = datetime ? moment(datetime).format('DD-MM-YYYY') : '';
  const paycheckYear = paycheck?.date.year;
  const paycheckMonth = paycheck?.date.month;
  const paycheckValue = paycheck ? [paycheck.date.month, paycheck.date.year].filter(Boolean).join('.') : '';
  const {calculate} = useTransferCalculationContext();
  const calculation = useMemo(
    () => calculate(formik.values, newPaycheck, paycheckYear, paycheckMonth),
    [formik.values, newPaycheck, calculate, paycheckYear, paycheckMonth]
  );
  const {isTaxAdjustmentOrForm101Absent, generalAdminNotes} = useTransfersByAgentContext();

  useEffect(() => {updateVat(calculation.vatValue);}, [calculation.vatValue]);

  const showPopup = () => {
    if (isEnabled) {
      getTransferData();
      handleShowModal();
    }
  };

  return (
    <>
      {isTaxAdjustmentOrForm101Absent
        ? <PopupDocsWarning actionType={'edit'} handleAction={() => showPopup()}/>
        : (
          <div
            className={isEnabled ? 'cursor-pointer' : 'opacity-30'}
            title={'עריכת העברה'}
            onClick={() => showPopup()}
          >✏️
          </div>
        )
      }
      <Modal show={show} onHide={handleCloseModal} dialogClassName={'extra-large-width'}>
        <Modal.Body className={styles.modalContainer}>
          <h3 className={styles.itemHeader}>
            <span className={'mx-1'}>
              <FormattedMessage
                id={'transfersByAgent-popup-edit-header-1'}
                defaultMessage={'Transferred to agent'}
              />
            </span>
            <span className={'mx-1'}>
              <FormattedMessage
                id={'transfersByAgent-popup-edit-create-header-2'}
                defaultMessage={'{agentId} - {agentName}'}
                values={{agentId, agentName: fullName}}
              />
            </span>
            <span className={'mx-1'}>
              <FormattedMessage
                id={'transfersByAgent-popup-edit-create-header-3'}
                defaultMessage={'{dateValue} (Paycheck {paycheckValue})'}
                values={{dateValue, paycheckValue}}
              />
            </span>
          </h3>
          <div>
            <Parameters
              formik={formik}
              getYearsOptions={getYearsOptions}
              newPaycheck={newPaycheck}
            />
            <div className={styles.tableSection}>
              <DataItemCheckbox
                fieldName={'allReceiptsChecked'}
                onChange={() => setAllReceiptsChecked()}
                inputClassname={'transfers-checkbox'}
                containerClassname={styles.checkAllCheckbox}
                checkboxSize={'transfers-checkbox-size'}
              />
              {useMemo(() => <Lines columns={columns} tableData={tableData}/>, [columns, tableData])}
            </div>
            <CustomLines values={formik.values}/>
            {useMemo(() => <Calculation
              formikValues={formik.values} {...calculation} />, [calculation, formik.values])}
          </div>
          <div className={styles.actions}>
            <button
              className={styles.btnCancel}
              onClick={() => {
                handleCloseModal();
                formik.resetForm();
              }}
            >
              <FormattedMessage id={'label-button-cancel'} defaultMessage={'Cancel'}/>
            </button>
            {formik.values.adminNotes || generalAdminNotes
              ? (
                <PopupWarning
                  generalNotes={generalAdminNotes}
                  transferNotes={formik.values.adminNotes}
                  disabled={!formik.isValid}
                  handleAction={() => {
                    handleEdit();
                    handleCloseModal();
                  }}
                />
              )
              : (
                <button
                  className={styles.btnSave}
                  disabled={!formik.isValid}
                  onClick={() => {
                    handleEdit();
                    handleCloseModal();
                  }}
                >
                  <FormattedMessage id={'transfersByAgent-popup-warning-save'} defaultMessage={'Save transfer'}/>
                </button>
              )
            }
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PopupEditTransfer;